import React, { useState, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import HTMLContentDisplay from '../molecules/HTMLContentDisplay';
import ContentMatchBadge from '../molecules/ContentMatchBadge';
import AgentContentLoader from '../molecules/AgentContentLoader';
import { findContentByMessage, getContentById, ContentBlock } from '../../services/contentManager';

interface DynamicContentAreaProps {
  isLoading?: boolean;
  sx?: any;
  candidate?: any;
  candidateId?: string;
  currentMessage?: string; // Latest chat message to determine content
  contentId?: string; // Specific content ID to display
  htmlContent?: string; // Direct HTML content to display (from agent API)
  onContentChange?: (contentId: string) => void; // Handler for content changes
  onActionClick?: (action: string, data?: any) => void; // Handler for action clicks
  onLoadComplete?: () => void; // Handler called when content loading is complete
}

const DynamicContentArea: React.FC<DynamicContentAreaProps> = ({
  isLoading = false,
  sx,
  candidate,
  candidateId,
  currentMessage,
  contentId,
  htmlContent,
  onContentChange,
  onActionClick,
  onLoadComplete,
}) => {
  const [currentContent, setCurrentContent] = useState<ContentBlock | null>(null);
  const [matchedKeywords, setMatchedKeywords] = useState<string[]>([]);
  const [matchedBy, setMatchedBy] = useState<'explicit' | 'keywords' | 'default'>('default');
  const [showBadge, setShowBadge] = useState(false);
  const [directHtmlContent, setDirectHtmlContent] = useState<string | null>(null);

  // Handle direct HTML content from agent API
  useEffect(() => {
    if (htmlContent) {
      console.log('[DynamicContentArea] Received direct HTML content from agent API');
      setDirectHtmlContent(htmlContent);
      setCurrentContent(null); // Clear regular content when showing HTML content
      
      // Notify parent that content loading is complete
      if (onLoadComplete) {
        onLoadComplete();
      }
    }
  }, [htmlContent, onLoadComplete]);

  useEffect(() => {
    // Skip regular content loading if we have direct HTML content
    if (htmlContent) {
      return;
    }
    
    let timer: NodeJS.Timeout | null = null;
    
    const loadContent = async () => {
      let content: ContentBlock;
      let matchType: 'explicit' | 'keywords' | 'default' = 'default';
      
      if (contentId && contentId !== '') {
        // Use specific content ID if provided (from clicking UI elements)
        content = getContentById(contentId);
        matchType = 'explicit';
        console.log('[DynamicContentArea] Loading content by ID:', contentId);
      } else if (currentMessage && currentMessage.trim() !== '') {
        // Find content based on chat message using API service
        content = await findContentByMessage(currentMessage, candidate);
        matchType = 'keywords'; // Use 'keywords' to trigger badge display
        console.log('[DynamicContentArea] Loading content via API for message:', currentMessage);
        
        // Set matched keywords to content ID for display
        setMatchedKeywords([content.id]);
      } else {
        // Default content
        content = getContentById('default');
        matchType = 'default';
        console.log('[DynamicContentArea] Loading default content');
      }
      
      setCurrentContent(content);
      setMatchedBy(matchType);
      setDirectHtmlContent(null); // Clear HTML content when showing regular content
      
      // Log content change for debugging
      if (content) {
        console.log('[DynamicContentArea] Current content:', {
          id: content.id,
          title: content.title,
          matchedBy: matchType,
          source: matchType === 'keywords' ? 'API Service' : matchType === 'explicit' ? 'User Click' : 'Default',
        });
      }
      
      // Show badge briefly when content changes via API (matchType === 'keywords')
      if (matchType === 'keywords') {
        setShowBadge(true);
        timer = setTimeout(() => setShowBadge(false), 5000);
      }
      
      // Notify parent that content loading is complete
      if (onLoadComplete) {
        onLoadComplete();
      }
    };

    loadContent();
    
    // Cleanup function to clear timer on unmount or re-render
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [currentMessage, contentId, candidate, htmlContent, onLoadComplete]);

  if (isLoading) {
    return <AgentContentLoader sx={{ ...sx }} />;
  }

  // Show direct HTML content if available
  if (directHtmlContent) {
    return (
      <Box sx={{ position: 'relative', height: '100%', overflow: 'auto', ...sx }}>
        <Box
          sx={{
            p: 3,
            height: '100%',
            width: '100%',
            maxWidth: '100%',
            overflow: 'auto',
            background: 'var(--white)',
            fontFamily: 'var(--font-body)',
            fontSize: 14,
            lineHeight: 1.6,
            '& h1, & h2, & h3': {
              fontFamily: 'var(--font-heading)',
              fontWeight: 700,
              marginTop: '20px',
              marginBottom: '12px',
              color: 'var(--primary-blue)',
            },
            '& h1': { fontSize: 28 },
            '& h2': { fontSize: 24 },
            '& h3': { fontSize: 20 },
            '& p': {
              marginBottom: '12px',
              color: 'var(--dark-gray)',
            },
            '& ul, & ol': {
              marginLeft: '24px',
              marginBottom: '12px',
              paddingLeft: '0',
            },
            '& li': {
              marginBottom: '8px',
              color: 'var(--dark-gray)',
            },
            '& strong': {
              fontWeight: 700,
              color: '#2c3e50',
            },
            '& em': {
              fontStyle: 'italic',
            },
            '& a': {
              color: 'var(--primary-blue)',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            },
            '& table': {
              width: '100%',
              borderCollapse: 'collapse',
              marginBottom: '16px',
            },
            '& th, & td': {
              padding: '12px',
              borderBottom: '1px solid #e0e7ff',
              textAlign: 'left',
            },
            '& th': {
              backgroundColor: '#f8faff',
              fontWeight: 700,
              color: 'var(--primary-blue)',
            },
            // Prevent content from breaking layout
            '& *': {
              maxWidth: '100% !important',
              boxSizing: 'border-box',
            },
            '& img': {
              maxWidth: '100%',
              height: 'auto',
            },
            // Override any width/min-width that might be set
            '& div, & main, & section': {
              width: 'auto !important',
              minWidth: 'auto !important',
            },
          }}
          dangerouslySetInnerHTML={{ __html: directHtmlContent }}
        />
      </Box>
    );
  }

  if (!currentContent) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          ...sx,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', height: '100%', ...sx }}>
      {/* Visual feedback badge */}
      {currentContent && (
        <ContentMatchBadge
          contentTitle={currentContent.title}
          matchedBy={matchedBy}
          matchedKeywords={matchedKeywords}
          show={showBadge && matchedBy === 'keywords'}
        />
      )}
      
      <HTMLContentDisplay 
        content={currentContent} 
        sx={{ height: '100%' }}
        candidate={candidate}
        candidateId={candidateId}
        onContentChange={onContentChange}
        onActionClick={onActionClick}
      />
    </Box>
  );
};

export default DynamicContentArea;
